import { MapPin, Phone, MessageCircle, Camera, Trophy, Frown } from "lucide-react";
import { Button } from "../ui/button";
import { BRAND, WA_LINK } from "../../lib/constants";

export const CENTRE_ADDRESS = "D613 Block D, Sangam Vihar, Ratiya Marg, Gali No. 3, New Delhi, Delhi 110080";
export const CENTRE_PHONE = "9910704971";

export const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const waMessage = (r) =>
  `Hi TASSEI, I completed the Discount Assessment.\n` +
  `Name: ${r.name}\nAssessment ID: ${r.assessment_id}\n` +
  `Correct Answers: ${r.score}/${r.total}\nDiscount Earned: ${r.discount}%\n` +
  `Valid Till: ${fmtDate(r.valid_till)}\n\nI would like to claim this discount. Please guide me.`;

const Stat = ({ label, value, testId, accent }) => (
  <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#64748B]">{label}</div>
    <div data-testid={testId} className={`mt-1 font-heading text-xl font-extrabold ${accent || "text-[#0A192F]"}`}>{value}</div>
  </div>
);

export const ResultCard = ({ result, previous = false }) => {
  const r = result;
  const won = r.discount > 0;
  const circumference = 326;
  const dash = circumference - (r.score / r.total) * circumference;

  return (
    <div data-testid="assessment-result" className="bg-white border border-slate-200 rounded-3xl p-7 md:p-9 shadow-xl shadow-slate-200/50">
      {previous && (
        <div data-testid="assessment-previous-notice" className="mb-6 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-sm px-5 py-4">
          You already completed this assessment. A new attempt is allowed only after your current discount expires on <b>{fmtDate(r.valid_till)}</b>.
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center gap-8">
        <div className="relative w-36 h-36 mx-auto md:mx-0 flex-shrink-0">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="52" stroke="#E2E8F0" strokeWidth="10" fill="none" />
            <circle cx="60" cy="60" r="52" stroke={won ? "#00B37E" : "#F59E0B"} strokeWidth="10" fill="none" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={dash} className="discount-score-ring" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span data-testid="result-score" className="font-heading text-3xl font-black text-[#0A192F]">{r.score}/{r.total}</span>
            <span className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">correct</span>
          </div>
        </div>

        <div className="flex-1 text-center md:text-left">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#00B37E]">
            {won ? <Trophy className="w-4 h-4" /> : <Frown className="w-4 h-4" />} {won ? "Congratulations" : "Better Luck Next Time"}
          </div>
          <h2 data-testid="result-headline" className="mt-2 font-heading text-3xl md:text-4xl font-black text-[#0A192F] leading-tight">
            {won ? <>You earned <span className="text-[#00B37E]">{r.discount}% OFF</span>, {r.name.split(" ")[0]}!</> : <>Better Luck Next Time, {r.name.split(" ")[0]}</>}
          </h2>
          <p className="mt-2 text-sm text-[#475569]">
            {won ? "Show this result at our centre to claim your course discount." : "You need at least 5 correct answers to earn a discount. You can try again after 30 days."}
          </p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Student" value={r.name} testId="result-name" />
        <Stat label="Assessment ID" value={r.assessment_id} testId="result-assessment-id" accent="text-[#00B37E]" />
        <Stat label="Correct" value={`${r.score} / ${r.total}`} testId="result-correct" />
        <Stat label="Discount" value={`${r.discount}%`} testId="result-discount" accent={won ? "text-[#00B37E]" : "text-amber-600"} />
        <Stat label="Valid Till" value={fmtDate(r.valid_till)} testId="result-valid-till" />
      </div>

      <div data-testid="result-claim-instructions" className="mt-8 rounded-2xl bg-[#0A192F] text-white p-6">
        <div className="flex items-start gap-3">
          <Camera className="w-5 h-5 text-[#7CF0BD] mt-0.5 flex-shrink-0" />
          <p className="text-sm leading-relaxed">
            To claim this discount, take a screenshot and visit us at: <b className="text-white">{CENTRE_ADDRESS}</b>. Call: <a href={`tel:+91${CENTRE_PHONE}`} className="text-[#7CF0BD] font-bold">{CENTRE_PHONE}</a>.
          </p>
        </div>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/70">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#7CF0BD]" /> Sangam Vihar, New Delhi 110080</span>
          <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#7CF0BD]" /> {BRAND.phone}</span>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <a href={WA_LINK(waMessage(r))} target="_blank" rel="noopener noreferrer" data-testid="result-whatsapp-btn">
          <Button type="button" className="w-full sm:w-auto bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold rounded-full h-12 px-7">
            <MessageCircle className="w-5 h-5 mr-2" /> Send Result on WhatsApp
          </Button>
        </a>
        <a href={`tel:+91${CENTRE_PHONE}`} data-testid="result-call-btn">
          <Button type="button" variant="outline" className="w-full sm:w-auto border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white font-semibold rounded-full h-12 px-7">
            <Phone className="w-4 h-4 mr-2" /> Call {CENTRE_PHONE}
          </Button>
        </a>
      </div>
    </div>
  );
};
